/* ----------------------------------------------------------------------------
 * HttpTtsAdapter — provider-neutral TTS over the SERVER proxy (go-live wiring).
 *
 * Implements the same TtsAdapter contract as the mock. synthesize() returns a
 * handle whose stream() lazily POSTs to /v1/ai/voice/tts and yields the binary
 * audio body in chunks; cancel() aborts the in-flight fetch — the barge-in
 * primitive. The browser sends ONLY the session cookie (+ CSRF); no provider
 * key is ever present client-side (the server holds it). On voice-unavailable
 * the error surfaces so useVoiceController falls back to an honest error state.
 * -------------------------------------------------------------------------- */

import type { TtsAdapter, TtsAudioChunk, TtsSynthesis, TtsSynthesizeOptions } from '../tts/TtsAdapter.ts';
import { synthesizeTts } from './voiceApi.ts';

class HttpTtsSynthesis implements TtsSynthesis {
  readonly provider: string;
  private readonly text: string;
  private readonly opts: TtsSynthesizeOptions;
  private readonly controller: AbortController;
  private cancelled = false;

  constructor(text: string, opts: TtsSynthesizeOptions) {
    this.text = text;
    this.opts = opts;
    this.provider = opts.provider ?? 'http';
    this.controller = new AbortController();
  }

  cancel(): void {
    this.cancelled = true;
    // Abort the network read so synthesis stops immediately (barge-in).
    try {
      this.controller.abort();
    } catch {
      /* no-op */
    }
  }

  async *stream(): AsyncGenerator<TtsAudioChunk, void, unknown> {
    if (this.cancelled) return;
    const res = await synthesizeTts(
      this.text,
      { voice: this.opts.voice, speed: this.opts.speed },
      this.controller.signal,
    );

    // Prefer a streaming reader for low-latency barge-in; fall back to a single
    // buffered chunk if the environment has no readable stream.
    const body = res.body;
    let seq = 0;
    if (body && typeof body.getReader === 'function') {
      const reader = body.getReader();
      try {
        for (;;) {
          if (this.cancelled) break;
          const { done, value } = await reader.read();
          if (done) break;
          if (value && value.byteLength > 0) {
            yield { seq, bytes: value };
            seq += 1;
          }
        }
      } finally {
        try {
          await reader.cancel();
        } catch {
          /* no-op */
        }
      }
      return;
    }

    const buf = new Uint8Array(await res.arrayBuffer());
    if (!this.cancelled && buf.byteLength > 0) {
      yield { seq: 0, bytes: buf };
    }
  }
}

export class HttpTtsAdapter implements TtsAdapter {
  readonly provider: string;

  constructor(provider: string) {
    this.provider = provider;
  }

  synthesize(text: string, opts: TtsSynthesizeOptions): TtsSynthesis {
    return new HttpTtsSynthesis(text, { ...opts, provider: opts.provider ?? this.provider });
  }
}

export { HttpTtsSynthesis };
